import { useState, useEffect } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface ClonePersonaDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (newPersonaName: string) => void;
  originalPersonaName?: string;
  onCloseAutoFocus?: (event: Event) => void;
}

export function ClonePersonaDialog({
  isOpen,
  onClose,
  onConfirm,
  originalPersonaName = "Persona",
  onCloseAutoFocus,
}: ClonePersonaDialogProps) {
  const [newPersonaName, setNewPersonaName] = useState(`${originalPersonaName} (clone)`);

  useEffect(() => {
    if (isOpen) {
      setNewPersonaName(`${originalPersonaName} (clone)`);
    }
  }, [originalPersonaName, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newPersonaName.trim()) {
      onConfirm(newPersonaName.trim());
      setNewPersonaName(`${originalPersonaName} (clone)`);
      onClose();
    }
  };

  const handleClose = () => {
    setNewPersonaName(`${originalPersonaName} (clone)`);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]" onCloseAutoFocus={onCloseAutoFocus}>
        <DialogHeader>
          <DialogTitle className="text-white">Clone Persona</DialogTitle>
          <DialogDescription className="sr-only">
            Enter a name for the cloned persona and confirm to create a copy.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="py-4">
            <Label htmlFor="cloneName" className="text-white">
              New Persona Name
            </Label>
            <Input
              id="cloneName"
              value={newPersonaName}
              onChange={(e) => setNewPersonaName(e.target.value)}
              className="mt-2 bg-surface-input border-surface-border text-white placeholder:text-muted focus:ring-primary"
              placeholder="Enter new persona name"
              autoFocus
            />
          </div>
          <DialogFooter>
            {/* Route Cancel through Radix's Close primitive so it triggers
                the same onOpenChange -> onCloseAutoFocus path Escape/X use.
                A plain onClick={handleClose} bypasses that and leaves focus
                on <body> after the Cancel-clicked button unmounts
                (DEF-792-02, WCAG 2.1 §2.4.3). */}
            <DialogClose asChild>
              <Button
                type="button"
                variant="outline"
                className="bg-surface border border-surface-border hover:bg-surface-hover hover:border-surface-border text-white"
              >
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              className="bg-primary hover:bg-primary-dark text-white"
              disabled={!newPersonaName.trim()}
            >
              Clone Persona
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}